<template>
  <div>
    <v-row no-gutters align="center" class="mb-3">
      <v-card outlined class="pa-3 flex-grow-1 mr-3">
        <div class="kpi-label">{{ $t("finance_model_label") }}</div>
        <div class="kpi-value">{{ paymentModelSummary }}</div>
      </v-card>
      <v-card outlined class="pa-3 flex-grow-1">
        <div class="kpi-label">{{ $t("finance_total_paid") }}</div>
        <div class="kpi-value">{{ formatCurrency(totalPaid) }}</div>
      </v-card>
      <primary-button v-if="isAdmin" class="ml-3" @click="dialogOpen = true">
        <v-icon left small>mdi-plus</v-icon>
        {{ $t("finance_new_platform_payment") }}
      </primary-button>
    </v-row>

    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="payments"
        :loading="loading"
        :loading-text="$t('finance_loading_payments')"
        mobile-breakpoint="0"
        :no-data-text="$t('finance_no_payment_items')"
        @click:row="openPayment"
      >
        <template #item.period="{ item }">
          <span class="font-weight-bold">{{ item.period }}</span>
        </template>
        <template #item.amount="{ item }">{{ formatCurrency(item.amount) }}</template>
        <template #item.date="{ item }">{{ localeDate(item.date) }}</template>
      </v-data-table>
    </v-card>

    <platform-payment-detail-dialog v-model="detailOpen" :payment="selectedPayment" />

    <platform-payment-form-dialog
      v-if="isAdmin"
      v-model="dialogOpen"
      :saving="saving"
      @save="onSavePayment"
    />
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import hasSnackbar from "@/mixins/hasSnackbar";
import PlatformPaymentDetailDialog from "./dialogs/PlatformPaymentDetailDialog.vue";
import PlatformPaymentFormDialog from "./dialogs/PlatformPaymentFormDialog.vue";

const mapPaymentDto = (dto) => ({
  id: dto.id,
  amount: Number(dto.amount) || 0,
  period: dto.period,
  note: dto.note,
  date: dto.created_at,
});

export default {
  name: "FinanceTabPlatformPayments",

  components: { PlatformPaymentDetailDialog, PlatformPaymentFormDialog },

  mixins: [hasSnackbar],

  data: () => ({
    paymentModel: null,
    totalPaid: 0,
    payments: [],
    loading: true,
    saving: false,
    detailOpen: false,
    selectedPayment: null,
    dialogOpen: false,
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },

    isAdmin() {
      return this.$store.state.user?.user?.type === "admin";
    },

    headers() {
      return [
        { text: this.$t("finance_period_label"), value: "period" },
        { text: this.$t("finance_amount"), value: "amount", align: "end" },
        { text: this.$t("finance_date"), value: "date" },
      ];
    },

    // paying_type/paying_month/paying_percent/paying_meal su postojeća polja
    // na restoranu (ista logika kao payingTypeOptions u RestaurantSettingsDialog).
    paymentModelSummary() {
      const model = this.paymentModel;
      const type = model ? Number(model.paying_type) : null;
      if (type === 1) return `${this.$t("by_month")}: ${this.formatCurrency(model.paying_month)}`;
      if (type === 2) return `${this.$t("by_percent")}: ${model.paying_percent}%`;
      if (type === 3) return `${this.$t("by_meal")}: ${this.formatCurrency(model.paying_meal)}`;
      return this.$t("finance_payment_model_not_set");
    },
  },

  created() {
    this.loadPayments();
  },

  methods: {
    async loadPayments() {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { data } = await restaurantFinanceService.getPlatformPayments(this.restaurantId);
        const payload = data?.data || {};
        this.paymentModel = payload.payment_model || null;
        this.totalPaid = Number(payload.total_paid) || 0;
        this.payments = (payload.payments || []).map(mapPaymentDto);
      } catch (error) {
        this.showErrorSnackbar(
          error?.response?.data?.message || this.$t("finance_platform_payments_load_error")
        );
      } finally {
        this.loading = false;
      }
    },

    openPayment(payment) {
      this.selectedPayment = payment;
      this.detailOpen = true;
    },

    async onSavePayment(payload) {
      this.saving = true;
      try {
        await restaurantFinanceService.createPlatformPayment(this.restaurantId, payload);
        this.showSuccessSnackbar(this.$t("finance_payment_saved"));
        this.dialogOpen = false;
        await this.loadPayments();
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_payment_save_error"));
      } finally {
        this.saving = false;
      }
    },

    formatCurrency(amount) {
      const value = Number(amount) || 0;
      return `${value.toFixed(2)} ${this.restaurantCurrency}`;
    },
  },
};
</script>

<style scoped>
.kpi-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.5);
}

.kpi-value {
  font-size: 1.05rem;
  font-weight: 700;
  margin-top: 4px;
}

::v-deep tbody tr {
  cursor: pointer;
}
</style>
